import { Condition } from '@/shared/types/condition';

import {
  ALL_CONDITION_ATTRIBUTES,
  ConditionAttributeConfig,
  ConditionInputType,
  EMPTY_CONDITION,
  SUBSTRING_COMPARISONS,
  SUBSTRING_MIN_LENGTH,
  VALUELESS_COMPARISONS,
} from './constants';

type Operator = Condition['operator'];

export type ConditionItem = { attribute?: string; comparison?: string; value?: string | null };

export type ConditionGroup = { operator: Operator; conditions: ConditionItem[] };

export type ConditionGroups = { operator: Operator; groups: ConditionGroup[] };

const DEFAULT_OPERATOR: Operator = 'and';

export const isConditionNested = (condition: Condition | undefined) =>
  Boolean(condition?.conditions?.some((child) => 'operator' in child));

// A bare condition next to a group is wrapped in its own group, so every child is a group
export const normalizeCondition = (condition: Condition): Condition => {
  const normalized = { ...condition, conditions: condition?.conditions ?? [] };

  if (!isConditionNested(normalized)) {
    return normalized;
  }

  return {
    ...normalized,
    conditions: normalized.conditions.map((child) =>
      Array.isArray(child?.conditions) ? child : { operator: 'or', conditions: [child] }
    ),
  };
};

const toItem = ({ attribute, comparison, value }: ConditionItem): ConditionItem => ({
  attribute,
  comparison,
  value: value ?? null,
});

export const toGroups = (condition: Condition | undefined): ConditionGroups => {
  const normalized = normalizeCondition(condition ?? EMPTY_CONDITION);

  if (isConditionNested(normalized)) {
    return {
      operator: normalized.operator ?? DEFAULT_OPERATOR,
      groups: normalized.conditions.map((group) => ({
        operator: group.operator ?? DEFAULT_OPERATOR,
        conditions: (group.conditions ?? []).map(toItem),
      })),
    };
  }

  if (normalized.conditions.length === 0) {
    return { operator: normalized.operator ?? DEFAULT_OPERATOR, groups: [] };
  }

  return {
    operator: DEFAULT_OPERATOR,
    groups: [
      {
        operator: normalized.operator ?? DEFAULT_OPERATOR,
        conditions: normalized.conditions.map(toItem),
      },
    ],
  };
};

export const fromGroups = ({ operator, groups }: ConditionGroups): Condition => {
  const nonEmpty = groups.filter((group) => group.conditions.length > 0);

  if (nonEmpty.length === 0) {
    return { operator, conditions: [] };
  }
  if (nonEmpty.length === 1) {
    return { operator: nonEmpty[0].operator, conditions: nonEmpty[0].conditions };
  }
  return { operator, conditions: nonEmpty };
};

const updateGroups = (
  condition: Condition,
  update: (value: ConditionGroups) => ConditionGroups
): Condition => fromGroups(update(toGroups(condition)));

const updateGroup = (
  condition: Condition,
  groupIndex: number,
  update: (group: ConditionGroup) => ConditionGroup
) =>
  updateGroups(condition, (value) => ({
    ...value,
    groups: value.groups.map((group, index) => (index === groupIndex ? update(group) : group)),
  }));

export const addConditionGroup = (condition: Condition, item: ConditionItem) =>
  updateGroups(condition, (value) => ({
    ...value,
    groups: [...value.groups, { operator: DEFAULT_OPERATOR, conditions: [item] }],
  }));

export const addCondition = (condition: Condition, groupIndex: number, item: ConditionItem) =>
  updateGroup(condition, groupIndex, (group) => ({
    ...group,
    conditions: [...group.conditions, item],
  }));

export const updateCondition = (
  condition: Condition,
  groupIndex: number,
  itemIndex: number,
  item: ConditionItem
) =>
  updateGroup(condition, groupIndex, (group) => ({
    ...group,
    conditions: group.conditions.map((current, index) => (index === itemIndex ? item : current)),
  }));

export const removeCondition = (condition: Condition, groupIndex: number, itemIndex: number) =>
  updateGroup(condition, groupIndex, (group) => ({
    ...group,
    conditions: group.conditions.filter((_, index) => index !== itemIndex),
  }));

export const removeConditionGroup = (condition: Condition, groupIndex: number) =>
  updateGroups(condition, (value) => ({
    ...value,
    groups: value.groups.filter((_, index) => index !== groupIndex),
  }));

export const setConditionGroupOperator = (
  condition: Condition,
  groupIndex: number,
  operator: Operator
) => updateGroup(condition, groupIndex, (group) => ({ ...group, operator }));

export const setConditionOperator = (condition: Condition, operator: Operator) =>
  updateGroups(condition, (value) => ({ ...value, operator }));

const PREFIXED_INPUTS: ConditionInputType[] = ['channel', 'status'];

export const isPrefixedInput = (input: ConditionInputType) => PREFIXED_INPUTS.includes(input);

export const getAttributeConfig = (attribute = ''): ConditionAttributeConfig | undefined =>
  ALL_CONDITION_ATTRIBUTES.find((config) => config.value === attribute) ??
  ALL_CONDITION_ATTRIBUTES.find(
    (config) => isPrefixedInput(config.input) && attribute.startsWith(`${config.value}.`)
  );

export const getAttributeOption = (attribute = '') => {
  const option = attribute.split('.').slice(1).join('.');
  return option === 'channel' || option === 'status' ? '' : option;
};

export const createCondition = (config: ConditionAttributeConfig): ConditionItem => ({
  attribute: config.value,
  comparison: config.comparisons[0],
  value: null,
});

export const isConditionItemComplete = ({ attribute, comparison, value }: ConditionItem) => {
  if (!attribute || !comparison) {
    return false;
  }
  if (VALUELESS_COMPARISONS.includes(comparison)) {
    return true;
  }
  const config = getAttributeConfig(attribute);
  if (config && isPrefixedInput(config.input)) {
    return Boolean(getAttributeOption(attribute));
  }
  if (config?.input === 'none') {
    return true;
  }
  return Boolean(value?.trim());
};

export const isConditionComplete = (condition: Condition | undefined) =>
  toGroups(condition).groups.every((group) => group.conditions.every(isConditionItemComplete));

const isShortSubstring = ({ comparison = '', value }: ConditionItem) =>
  SUBSTRING_COMPARISONS.includes(comparison) && !!value && value.length < SUBSTRING_MIN_LENGTH;

export const hasShortSubstringValue = (condition: Condition | undefined) =>
  toGroups(condition).groups.some((group) => group.conditions.some(isShortSubstring));
